import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteList } from '../../hooks/useInfiniteList';
import { Input, Switch } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CiSearch } from 'react-icons/ci';
import { MdLock, MdLockOpen, MdAddAPhoto, MdClose, MdCampaign } from 'react-icons/md';
import branch from 'branch-sdk';

import CustomModal from '../Modal/CustomModal';
import { ModalHeader, ModalBody, ModalFooter, ModalButton, ModalTabs } from '../Modal/ModalParts';
import { Avatar, EmptyState, titleCase } from './ListRow';
import { Loader } from '..';
import { Colors, Utils } from '../../config';
import {
  CHANNEL_TYPE,
  CONTACTS_PAGE_SIZE,
  GROUP_LIMITS,
  MAX_AVATAR_BYTES,
  MAX_AVATAR_LABEL,
  ROUTES,
  SEARCH_DEBOUNCE_MS,
} from '../../config/constants';
import { AppAction, MsgAction } from '../../store/actions';

const MODE = { DIRECT: 'direct', GROUP: 'group' };
const STEP = { MEMBERS: 'members', DETAILS: 'details' };

const TABS = [
  { value: MODE.DIRECT, label: 'New chat' },
  { value: MODE.GROUP, label: 'New group' },
];

const GROUP_STEPS = [
  { key: STEP.MEMBERS, label: 'Members' },
  { key: STEP.DETAILS, label: 'Details' },
];

/**
 * One popup for both ways of starting a conversation. Replaces the old
 * login-ID-only create-chat modal and the standalone New Group page: contacts
 * are listed directly, tapping one opens a chat, or several can be collected
 * into a group.
 *
 * Group creation is a two-step wizard — pick the members, then name the group
 * and give it a picture — so the form isn't competing with the contact list for
 * the same scroll.
 */
const NewConversationModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const avatarInputRef = useRef(null);

  const contacts = useSelector((state) => state.AppReducer.contacts?.rows) || [];
  const me = useSelector((state) => state?.AppReducer?.user?.data);
  const contactsLoading = useSelector((state) => state.AppReducer.loader);
  const creating = useSelector((state) => state?.MsgReducer?.group_loader);

  const [mode, setMode] = useState(MODE.DIRECT);
  const [step, setStep] = useState(STEP.MEMBERS);
  const [query, setQuery] = useState('');
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [isBroadcast, setIsBroadcast] = useState(false);
  const [picked, setPicked] = useState([]);
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  /**
   * Contacts are the source for both modes, paged on scroll.
   *
   * This asked for 200 in one request and rendered the result as though it were the
   * whole address book, so contact 201 could not be reached and nothing on screen said
   * so. The `count` was in the response the whole time and ignored.
   *
   * The search moved to the server with the paging: filtering the loaded page locally
   * would search whichever 20 contacts happened to be in hand and report "no matches"
   * for someone who is simply further down the list.
   */
  const [term, setTerm] = useState('');

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed === term) return undefined;
    const timer = setTimeout(() => setTerm(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, term]);

  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;

  const fetchContacts = useCallback(
    (cursor) =>
      dispatch(
        AppAction.ListContacts({
          limit: CONTACTS_PAGE_SIZE,
          offset: cursor === null ? 0 : contactsRef.current.length,
          search: term,
        })
      ),
    [dispatch, term]
  );

  // `enabled` parks the hook while the popup is closed, so it neither fetches nor
  // observes; `resetKey` folds in the term, so a new search rewinds to the first page
  // instead of appending one term's results onto another's.
  const { sentinelRef, exhausted: contactsExhausted } = useInfiniteList({
    resetKey: `contacts:${term}`,
    fetchPage: fetchContacts,
    loading: contactsLoading,
    loadedCount: contacts.length,
    nextCursor: undefined,
    enabled: open,
  });

  // Reset when the popup closes so it never reopens mid-flow.
  useEffect(() => {
    if (!open) {
      setMode(MODE.DIRECT);
      setStep(STEP.MEMBERS);
      setQuery('');
      setGroupName('');
      setDescription('');
      setIsSecret(false);
      setIsBroadcast(false);
      setPicked([]);
      setTouched({});
      setBusy(false);
      setAvatarFile(null);
      setAvatarPreview(null);
    }
  }, [open]);

  // Switching tabs restarts the wizard rather than stranding it on step two.
  useEffect(() => {
    setStep(STEP.MEMBERS);
  }, [mode]);

  // Object URLs are revoked on replace/unmount; leaving them alive holds the
  // whole file in memory for the life of the tab.
  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  // No local filtering — the server applied the search, so what is held IS the result.
  const contactMatches = useMemo(
    () =>
      contacts
        .map((c) => ({
          id: c.contact_user?.id ?? c.contact_user_id,
          name: titleCase(c.nickname || c.contact_user?.first_name || 'Unknown'),
          meta: c.contact_user?.login_id ? `ID ${c.contact_user.login_id}` : 'Contact',
          avatar: c.contact_user?.media?.media_thumb_url || c.contact_user?.media?.media_url,
          known: true,
        }))
        .filter((p) => p.id),
    [contacts]
  );

  // Fall back to the directory only when no contact matches, so you can still
  // reach someone you have never spoken to — the old search-by-login-ID path.
  const foundUser = useSelector((state) => state?.MsgReducer?.search_by_id?.data);
  const searching = useSelector((state) => state?.MsgReducer?.loader);
  // Only once the server has answered for this term — otherwise the directory lookup
  // fires against a list that simply has not been re-fetched yet.
  const noContactMatch =
    term.length > 0 && term === query.trim() && !contactsLoading && contactMatches.length === 0;

  useEffect(() => {
    if (!open || !noContactMatch) return;
    const q = query.trim();
    const timer = setTimeout(() => dispatch(MsgAction.CreateGroupAddId(q)), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, noContactMatch, query, dispatch]);

  const directoryMatches = useMemo(() => {
    if (!noContactMatch || !foundUser?.id) return [];
    if (String(foundUser.id) === String(me?.id)) return [];
    return [
      {
        id: foundUser.id,
        name: titleCase(foundUser.first_name || 'Unknown'),
        meta: foundUser.login_id ? `ID ${foundUser.login_id} · not in contacts` : 'Not in contacts',
        avatar: foundUser.media?.media_thumb_url || foundUser.media?.media_url,
        known: false,
      },
    ];
  }, [noContactMatch, foundUser, me?.id]);

  const people = noContactMatch ? directoryMatches : contactMatches;

  const isPicked = (id) => picked.some((p) => p.id === id);

  const togglePick = (person) => {
    setPicked((prev) =>
      prev.some((p) => p.id === person.id)
        ? prev.filter((p) => p.id !== person.id)
        : [...prev, person]
    );
  };

  const pickAvatar = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Utils.showMessage('error', 'Group picture must be an image');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      Utils.showMessage('error', `Group picture must be under ${MAX_AVATAR_LABEL}`);
      return;
    }
    setAvatarFile(file);
  };

  const startDirectChat = (person) => {
    setBusy(true);
    dispatch(
      MsgAction.CreateChatChannel(
        { receiver_id: person.id, channel_type: CHANNEL_TYPE.CHAT },
        (data) => {
          setBusy(false);
          onClose();
          Utils.showMessage('success', 'Chat created successfully');
          dispatch(MsgAction.SelectedChatUser(data?.data));
          dispatch(MsgAction.GlobalSearchChatsGroups());

          // Someone reached through the directory becomes a contact.
          if (!person.known) {
            dispatch(
              AppAction.CreateContact(
                { contact_user_id: person.id, nickname: person.name || 'Unknown' },
                () => {}
              )
            );
          }

          navigate(`${ROUTES.MESSAGES}/${data?.data?.chat_member?.member_id}`);
        }
      )
    );
  };

  // Mirrors the flow the New Group page used: create the group, add the
  // picked members, then register a Branch invite link for it.
  const createGroup = () => {
    setTouched({ name: true, description: true, members: true });
    if (!isValid) return;
    setBusy(true);

    const formData = new FormData();
    formData.append('name', groupName.trim());
    formData.append('description', description.trim());
    formData.append('is_secret', isBroadcast ? false : isSecret);
    formData.append('is_broadcast', isBroadcast);
    if (avatarFile) formData.append('imageFile', avatarFile);

    dispatch(
      MsgAction.CreateGroup(formData, (data) => {
        const group = data?.data;

        // The server also rejects a name already in use; it surfaces its own
        // message, so just hand the form back.
        if (!group?.id) {
          setBusy(false);
          return;
        }

        dispatch(
          MsgAction.AddUsersGroup(
            {
              group_id: group?.id,
              user_ids: picked.map((p) => p.id),
              is_secret: group?.is_secret,
            },
            () => {}
          )
        );

        branch.link(
          {
            data: {
              group_id: group?.id,
              group_name: group?.name,
              isSecret: group?.is_secret,
              custom_title: 'Join our group!',
              $desktop_url: process.env.REACT_APP_DESKTOP_URL || window.location.origin,
            },
            tags: [`group-invite-${group?.id}`],
            channel: `web-${group?.id}`,
            feature: `group_join-${group?.id}`,
          },
          () => {}
        );

        setBusy(false);
        onClose();
        Utils.showMessage('success', 'Group created successfully');
        dispatch(MsgAction.SelectedChatUser(group));
        dispatch(MsgAction.GlobalSearchChatsGroups());
        navigate(`${ROUTES.MESSAGES}/${group?.id}`);
      })
    );
  };

  const groupMode = mode === MODE.GROUP;
  const onDetailsStep = groupMode && step === STEP.DETAILS;

  // Mirrors chat.validation.js on the backend so the rules are enforced
  // before the request rather than only in the error toast.
  const errors = {
    name: (() => {
      const v = groupName.trim();
      if (!v) return 'Name is required';
      if (v.length < GROUP_LIMITS.name.min || v.length > GROUP_LIMITS.name.max) {
        return `Name must be between ${GROUP_LIMITS.name.min} - ${GROUP_LIMITS.name.max} char`;
      }
      return null;
    })(),
    description: (() => {
      const v = description.trim();
      if (!v) return 'Description is required';
      if (v.length < GROUP_LIMITS.description.min || v.length > GROUP_LIMITS.description.max) {
        return `Description must be between ${GROUP_LIMITS.description.min} - ${GROUP_LIMITS.description.max} char`;
      }
      return null;
    })(),
    members: picked.length === 0 ? 'Pick at least one member' : null,
  };

  const isValid = !errors.name && !errors.description && !errors.members;
  const canCreate = onDetailsStep && isValid && !busy;
  const showError = (key) => (touched[key] ? errors[key] : null);

  /**
   * One submit handler for the wizard: Enter and the primary button both land
   * here, and which action runs is decided by the step rather than by which
   * control was used.
   *
   * Direct mode has no primary action — a chat starts by picking a person — so
   * Enter in the search box is swallowed rather than doing something arbitrary.
   */
  const submitStep = (event) => {
    event.preventDefault();
    if (!groupMode) return;
    if (onDetailsStep) {
      createGroup();
      return;
    }
    if (picked.length > 0) setStep(STEP.DETAILS);
  };

  return (
    <CustomModal
      open={open}
      cancelModal={onClose}
      centered
      destroyOnClose
      width={480}
      Children={
        /**
         * A real form, so Enter submits whatever the current step's primary
         * button does — Next while picking members, Create on the details step.
         * Both used to be click-only: typing a group name and pressing Enter did
         * nothing at all, which is the one thing everyone tries.
         *
         * `noValidate` because the field rules live in `errors` below and are
         * already shown inline; the browser's own bubbles would duplicate them in
         * a style that belongs to no part of this design.
         */
        <form style={{ width: '100%' }} noValidate onSubmit={submitStep}>
          <ModalHeader
            title={groupMode ? 'New group' : 'New chat'}
            subtitle={
              !groupMode
                ? 'Pick a contact to start a conversation.'
                : onDetailsStep
                  ? 'Name the group and give it a picture.'
                  : 'Choose who joins the group.'
            }
            onClose={onClose}
          />

          <ModalTabs tabs={TABS} value={mode} onChange={setMode} />

          {groupMode && (
            <div className="wizard-steps">
              {GROUP_STEPS.map((s, i) => {
                const active = step === s.key;
                const done = s.key === STEP.MEMBERS && step === STEP.DETAILS;
                return (
                  <span
                    key={s.key}
                    className={`wizard-step ${active ? 'is-active' : ''} ${done ? 'is-done' : ''}`}
                  >
                    <span className="wizard-step-dot">
                      <span>{done ? '✓' : i + 1}</span>
                    </span>
                    {s.label}
                  </span>
                );
              })}
            </div>
          )}

          {onDetailsStep ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  className="avatar-picker"
                  title="Add a group picture"
                  onClick={() => avatarInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      avatarInputRef.current?.click();
                    }
                  }}
                >
                  {avatarPreview ? (
                    <>
                      <img src={avatarPreview} alt="Group" />
                      <button
                        type="button"
                        className="avatar-picker-clear"
                        aria-label="Remove picture"
                        title="Remove picture"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAvatarFile(null);
                        }}
                      >
                        <MdClose size={14} />
                      </button>
                    </>
                  ) : (
                    <MdAddAPhoto size={24} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>
                    Group picture
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    Optional. PNG or JPG, up to {MAX_AVATAR_LABEL}.
                  </div>
                </div>
              </div>

              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={pickAvatar}
              />

              <div className="group-field">
                <Input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  placeholder="Group name — e.g. Release Crew"
                  maxLength={GROUP_LIMITS.name.max}
                  status={showError('name') ? 'error' : ''}
                />
                <div className="group-field-foot">
                  <span className="group-field-error">{showError('name')}</span>
                  <span className="group-field-count">
                    {groupName.trim().length}/{GROUP_LIMITS.name.max}
                  </span>
                </div>
              </div>

              <div className="group-field">
                <Input.TextArea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, description: true }))}
                  placeholder="What is this group for?"
                  maxLength={GROUP_LIMITS.description.max}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  status={showError('description') ? 'error' : ''}
                />
                <div className="group-field-foot">
                  <span className="group-field-error">{showError('description')}</span>
                  <span className="group-field-count">
                    {description.trim().length}/{GROUP_LIMITS.description.max}
                  </span>
                </div>
              </div>

              <div className="group-secret">
                <span className="settings-tile-glyph" style={{ width: 34, height: 34 }}>
                  <MdCampaign size={17} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
                    Broadcast channel
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                    Only admins can post. Everyone else can read and react, but not reply.
                  </span>
                </span>
                <Switch
                  checked={isBroadcast}
                  onChange={(next) => {
                    setIsBroadcast(next);
                    if (next) setIsSecret(false);
                  }}
                />
              </div>

              <div className="group-secret">
                <span className="settings-tile-glyph" style={{ width: 34, height: 34 }}>
                  {isSecret ? <MdLock size={17} /> : <MdLockOpen size={17} />}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
                    Secret group
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                    {isBroadcast
                      ? 'Not available for broadcast channels.'
                      : 'Hidden from search — members join by invite only.'}
                  </span>
                </span>
                <Switch
                  checked={isSecret}
                  disabled={isBroadcast}
                  onChange={(next) => {
                    setIsSecret(next);
                    if (next) setIsBroadcast(false);
                  }}
                />
              </div>
            </>
          ) : (
            <>
              {groupMode && picked.length > 0 && (
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}
                  aria-label="Selected members"
                >
                  {picked.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="member-chip"
                      onClick={() => togglePick(p)}
                      title="Remove"
                    >
                      <Avatar name={p.name} seed={p.id} src={p.avatar} size={22} radius="50%" />
                      {p.name.split(' ')[0]} ✕
                    </button>
                  ))}
                </div>
              )}

              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search contacts, or a login ID"
                prefix={<CiSearch />}
                style={{ marginBottom: 10 }}
              />

              <ModalBody scroll>
                {(contactsLoading || (noContactMatch && searching)) && people.length === 0 ? (
                  <Loader.Circular size={40} color={Colors.redColor} />
                ) : people.length === 0 ? (
                  <EmptyState>
                    {query
                      ? `No contact or user matches “${query}”.`
                      : 'No contacts yet. Search by name or login ID.'}
                  </EmptyState>
                ) : (
                  people.map((person) => {
                    const checked = groupMode && isPicked(person.id);
                    return (
                      <div
                        key={person.id}
                        role="button"
                        tabIndex={0}
                        className={`person-row ${checked ? 'is-checked' : ''}`}
                        onClick={() => (groupMode ? togglePick(person) : startDirectChat(person))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            groupMode ? togglePick(person) : startDirectChat(person);
                          }
                        }}
                      >
                        <Avatar name={person.name} seed={person.id} src={person.avatar} size={40} />
                        <span className="person-row-text">
                          <span className="person-row-name">{person.name}</span>
                          <span className="person-row-meta">{person.meta}</span>
                        </span>
                        {checked ? (
                          <span className="person-check">✓</span>
                        ) : (
                          <span style={{ color: 'var(--faint)', fontSize: 17, lineHeight: 1 }}>
                            ›
                          </span>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Contacts page on scroll. Not rendered while the directory fallback
                    is showing — those rows are a single lookup, not a paged list. */}
                {!noContactMatch && !contactsExhausted && people.length > 0 && (
                  <div ref={sentinelRef} aria-hidden="true" />
                )}
              </ModalBody>
            </>
          )}

          {groupMode && (
            <ModalFooter
              note={
                onDetailsStep
                  ? `${picked.length} member${picked.length > 1 ? 's' : ''} selected`
                  : picked.length
                    ? `${picked.length} member${picked.length > 1 ? 's' : ''} selected`
                    : 'Pick at least one member'
              }
            >
              {/* The primary button is a submit, so the click path and the Enter
                  path are the same code — they cannot drift apart. */}
              {onDetailsStep ? (
                <>
                  <ModalButton variant="ghost" onClick={() => setStep(STEP.MEMBERS)}>
                    Back
                  </ModalButton>
                  <ModalButton type="submit" variant="accent" disabled={!canCreate}>
                    {busy || creating ? 'Creating…' : 'Create group'}
                  </ModalButton>
                </>
              ) : (
                <>
                  <ModalButton variant="ghost" onClick={onClose}>
                    Cancel
                  </ModalButton>
                  <ModalButton type="submit" variant="accent" disabled={picked.length === 0}>
                    Next
                  </ModalButton>
                </>
              )}
            </ModalFooter>
          )}
        </form>
      }
    />
  );
};

export default NewConversationModal;
